/**
 * HTTP 请求配置和拦截器管理
 *
 * 这个文件负责配置和管理应用程序的 HTTP 请求，包括：
 * - Axios 实例配置
 * - 请求拦截器（添加安全验证、MD5 加密）
 * - 响应拦截器（错误处理、状态码处理）
 * - 全局错误处理
 * - 登录状态管理
 */

import { toast } from '@/components/bs-ui/toast/use-toast'
import { triggerLenovoBack } from '@/utils/lenovoBack'
import { reloadCurrentApp } from '@/utils/reloadCurrentApp'
import { redirectToSmbLoginHas } from '@/utils/smbLogin'
import axios from 'axios'
import i18next from 'i18next'
import { md5Encrypt, requestNeedVerify } from './utils'

// 设置 axios 默认携带凭证
axios.defaults.withCredentials = true

const ALLOW_CODES_API_PATH = '/api/v1/permission/allow_codes'
const ALLOW_CODES_FAILURE_DATA = 'token获取百应菜单权限失败'

const createHandledError = (message: string) => {
  const handledError = new Error(message) as Error & { __handled?: boolean }
  handledError.__handled = true
  return handledError
}

/**
 * 统一处理登录过期（401）：清除本地凭证并根据登录模式跳转
 * 成功拦截器（业务码 401）和错误拦截器（HTTP 状态码 401）共用此函数
 */
const handleUnauthorized = () => {
  console.error('登录过期 :>> ')
  const UUR_INFO = 'UUR_INFO'
  const infoStr = localStorage.getItem(UUR_INFO)
  localStorage.removeItem(UUR_INFO)
  localStorage.removeItem('lenovo-token')

  const feMode = localStorage.getItem('fe-login-mode')
  const mode = feMode ? parseInt(feMode) : 2
  if (mode === 2) {
    // 模式 2：微前端场景下交给宿主执行顶层跳转
    triggerLenovoBack({
      backUrl: localStorage.getItem('lenovo-back-url') || 'https://smbpaasadmin.lenovo.com'
    })
    return
  }
  // 其他模式：重新加载页面
  infoStr && location.reload()
}

const isAllowCodesFailure = (url?: string, responseData?: any) => {
  return (
    typeof url === 'string' &&
    url.includes(ALLOW_CODES_API_PATH) &&
    responseData?.status_code === 400 &&
    responseData?.data === ALLOW_CODES_FAILURE_DATA
  )
}

/**
 * 创建自定义 Axios 实例
 * 配置请求超时时间和其他默认设置
 */
const customAxios = axios.create({
  // 配置
  timeout: 5 * 60 * 1000 // 请求超时时间：5分钟
})

/**
 * 请求拦截器
 * 在发送请求前对请求配置进行处理
 */
customAxios.interceptors.request.use(
  config => {
    console.log('request config :>>>>>>>>>> ', config)
    // 处理请求头，增加参数的 MD5 做安全验证，目前只支持 json 格式
    if (requestNeedVerify(config.method, config.url)) {
      console.log('需要验证:', config.data)
      try {
        // 为需要验证的请求添加 MD5 加密头
        config.headers.Encrypt = md5Encrypt(JSON.stringify(config.data))
      } catch (error) {
        console.error('data not json：', config.data)
      }
    }

    return config
  },
  error => {
    // 请求配置错误处理
    return Promise.reject(error)
  }
)

/**
 * 响应拦截器
 * 处理服务器响应，包括成功和错误状态的处理
 */
customAxios.interceptors.response.use(
  function (response) {
    // 提取响应中的状态码和消息
    const code = response.data.status_code || response.data.code
    const message = response.data.status_message || response.data.message
    const err_msg = message || '未知异常'
    console.log('response code, message :>> ', code, message)

    // 成功状态码处理 (200-299)
    if (code >= 200 && code < 300) {
      return response.data.data
    } else if (code === 401) {
      // 业务码 401：登录过期，触发统一跳转逻辑
      handleUnauthorized()
      return Promise.reject(createHandledError('Invalid auth credentials'))
    } else if (code) {
      if (isAllowCodesFailure(response.config?.url, response.data)) {
        redirectToSmbLoginHas()
        return Promise.reject(createHandledError(ALLOW_CODES_FAILURE_DATA))
      }
      // 错误状态码处理
      let error_info = err_msg
      if (typeof err_msg === 'object') {
        try {
          error_info = JSON.stringify(err_msg)
        } catch (error) {
          console.error('status_message not json')
        }
      } else {
        error_info = String(err_msg)
      }

      /* 添加跳过全局错误处理
      const res: any = await singleNodeRun(data, { headers: { 'X-Skip-Global-Error': 'true' } })
      */
      if (response.config?.headers?.['X-Skip-Global-Error'] === 'true') {
        return Promise.reject(error_info)
      }

      // 如果错误代码为 10404，跳转到首页
      if (code === 10404) {
        // 跳转到首页
        setTimeout(() => {
          reloadCurrentApp()
        }, 1000)
        return Promise.reject(error_info)
      }

      // 显示错误提示
      if (error_info) {
        toast({
          title: `${i18next.t('prompt')}`,
          variant: 'error',
          description: `${error_info}`
        })
      }
    }
    return Promise.reject(err_msg)
  },
  /**
   * 响应错误处理函数
   * 处理网络错误、HTTP 状态码错误等异常情况
   */
  function (error) {
    console.error('application error :>> ', error)
    if (isAllowCodesFailure(error.config?.url, error.response?.data)) {
      redirectToSmbLoginHas()
      return Promise.reject(createHandledError(ALLOW_CODES_FAILURE_DATA))
    }

    // 检查是否跳过全局错误处理
    if (error.config?.headers?.['X-Skip-Global-Error'] === 'true') {
      return Promise.reject(error)
    }

    // 处理 401 未授权错误（登录过期）
    if (error.response?.status === 401) {
      handleUnauthorized()
      return Promise.reject(error)
    }

    // 构建错误消息
    let message = error.response?.status ? `${error.response?.status}：${error.message}` : error.message

    // 处理 403 禁止访问错误
    if (error.response?.status === 403) {
      message = `403：暂无该功能权限，请联系企业超级管理员`
    }

    // 如果错误代码为 10404，跳转到首页
    if (error.response?.data?.code === 10404) {
      // 跳转到首页
      setTimeout(() => {
        reloadCurrentApp()
      }, 1000)
      // 提前 return，不显示 toast
      return Promise.reject(error)
    }

    // 显示错误提示弹窗
    toast({
      title: `${i18next.t('prompt')}`,
      variant: 'error',
      description: message
    })
    // window.errorAlerts([error.message])
    return Promise.reject(error)
  }
)

// 导出自定义 Axios 实例
export default customAxios

/**
 * 判断是否为外部资源 URL（OSS、CDN 等第三方服务）
 * 这些服务通常返回 Access-Control-Allow-Origin: *，不支持 credentials
 */
function isExternalResourceUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url)
    // 外部资源域名列表（OSS、CDN 等）
    const externalHosts = [
      'aliyuncs.com',
      'oss-cn-beijing.aliyuncs.com',
      'oss-cn-hangzhou.aliyuncs.com',
      'oss-cn-shanghai.aliyuncs.com',
      'cloudfront.net',
      'amazonaws.com',
      'cdn.' // 通用 CDN 前缀
    ]
    return externalHosts.some(ext => hostname.includes(ext))
  } catch {
    // 相对路径 /api/xxx 等，视为内部请求
    return false
  }
}

/**
 * 判断 fetch 响应是否为 SSE。后端常见写法：text/event-stream、带 charset、大小写不一；
 * 若用全量字符串 === 'text/event-stream; charset=utf-8' 会导致误判为 JSON，先 response.json() 再 getReader 会读空流或抛错。
 */
export function isFetchSseResponse(response: Response): boolean {
  const ct = response.headers.get('content-type') || ''
  return ct.toLowerCase().includes('text/event-stream')
}

/**
 * 智能 fetch 封装：
 * - 外部资源（OSS、CDN等）：不带凭证，避免 CORS 错误
 * - 其他请求：携带 credentials: 'include'（微前端跨域需要）
 *
 * @param input - 请求的 URL 或 Request 对象
 * @param init - 可选的请求配置
 * @param skipCredentials - 跳过自动 credentials 处理，使用原生 fetch 默认行为
 * @returns fetch 响应的 Promise
 */
export function customFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  skipCredentials?: boolean
): Promise<Response> {
  // 跳过自动处理，直接用原生 fetch
  if (skipCredentials) {
    return fetch(input, init)
  }

  const urlStr = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url

  const mergedInit: RequestInit = {
    ...init,
    credentials: isExternalResourceUrl(urlStr) ? 'omit' : 'include'
  }

  return fetch(input, mergedInit)
}

/**
 * 接口异常提示弹窗高阶函数
 * 用于包装 API 函数，自动处理错误并显示提示
 *
 * @param apiFunc - 要包装的 API 函数
 * @param iocFunc - 可选的错误处理回调函数
 * @returns 包装后的函数，会自动捕获错误并显示提示
 */
export function captureAndAlertRequestErrorHoc(apiFunc, iocFunc?) {
  return apiFunc.catch(error => {
    if (error === null) return // app error

    console.log('error :>> ', error)
    iocFunc?.(error)
    // 显示错误提示弹窗
    toast({
      title: `${i18next.t('prompt')}`,
      variant: 'error',
      description: typeof error === 'string' ? error : JSON.stringify(error)
    })
    console.error('逻辑异常 :>> ', error)
    return false
  })
}
